import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Tent, 
  Mountain, 
  Trees, 
  Sun, 
  Star,  
  Share2, 
  Shield, 
  Zap, 
  ArrowRight,
  CheckCircle,
  Smartphone,
  Globe,
  Heart,
  Moon,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Users,
  Target,
  Award,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';

const LandingPage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [aboutRef, aboutInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [statsRef, statsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [questionsRef, questionsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', href: '#home', icon: <Tent className="w-4 h-4" />, action: () => scrollToSection('home') },
    { name: 'About', href: '#about', icon: <Users className="w-4 h-4" />, action: () => scrollToSection('about') },
    { name: 'Features', href: '#features', icon: <Zap className="w-4 h-4" />, action: () => scrollToSection('features') },
    { name: 'Questions', href: '#questions', icon: <HelpCircle className="w-4 h-4" />, action: () => scrollToSection('questions') },
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Availability",
      description: "Get instant updates on campsite availability across multiple campgrounds with live data synchronization."
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Smart Sharing",
      description: "Share availability information with friends and family through email and WhatsApp notifications."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure & Reliable",
      description: "Your data is protected with enterprise-grade security and reliable uptime monitoring."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile Optimized",
      description: "Perfect experience across all devices with responsive design and touch-friendly interface."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Multi-Location Search",
      description: "Search multiple campgrounds simultaneously and compare availability across different locations."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "User-Friendly",
      description: "Intuitive interface designed for outdoor enthusiasts with seamless user experience."
    }
  ];

  const aboutFeatures = [
    {
      icon: <Target className="w-12 h-12 text-emerald-600" />,
      title: "Our Mission",
      description: "To simplify the camping experience by providing real-time campsite availability information, helping outdoor enthusiasts discover and secure their perfect camping spots effortlessly."
    },
    {
      icon: <Award className="w-12 h-12 text-teal-600" />,
      title: "Why We Built This",
      description: "Frustrated by sold-out campsites and endless refreshing of booking websites, we created CampSite Checker to solve the real problems campers face when trying to book their dream destinations."
    },
    {
      icon: <Users className="w-12 h-12 text-teal-600" />,
      title: "Our Community",
      description: "Join thousands of outdoor enthusiasts who trust our platform to find available campsites, share discoveries, and plan incredible adventures across the nation's most beautiful locations."
    }
  ];

  const stats = [
    { number: "10,000+", label: "Happy Campers" },
    { number: "3000+", label: "Campgrounds" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" }
  ];

  const faqs = [
    {
      question: "How does CampSite Checker work?",
      answer: "CampSite Checker monitors campground availability in real-time across multiple platforms and sends you instant notifications when your desired campsites become available. Simply set your preferences, and we'll do the rest."
    },
    {
      question: "Is CampSite Checker free to use?",
      answer: "We offer a premium-only plan at a very affordable price. Enjoy unlimited campground searches, priority notifications, and advanced filtering options — all for a low monthly fee."
    },
     {
    question: "Can I cancel my subscription at any time?",
    answer: "Yes, you can cancel your subscription anytime from your account settings. Your access will remain active until the end of your billing cycle."
    },
    {
      question: "Which campgrounds are supported?",
      answer: "We support over 3,000 campgrounds across national parks, state parks, and private campgrounds. Our database includes popular destinations like Yosemite, Yellowstone, Grand Canyon, and many more."
    },
    {
      question: "How accurate are the availability updates?",
      answer: "Our system checks availability every 5 minutes during peak times and every 15 minutes during off-peak hours. We maintain 99.9% accuracy by directly connecting to official reservation systems."
    },
    {
      question: "Can I share availability with friends and family?",
      answer: "Absolutely! Our smart sharing feature allows you to send availability updates via email, text, or WhatsApp. You can also create group notifications for planning trips with multiple people."
    },
    {
      question: "Do you offer mobile apps?",
      answer: "Our web application is fully responsive and works perfectly on all mobile devices. We're currently developing native iOS and Android apps which will be available soon."
    }
  ];

  const themeClass = isDarkMode 
    ? 'min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white' 
    : 'min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50';

  return (
    <div className={themeClass}>
      <AnimatedBackground />
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-lg shadow-lg border-b ${isDarkMode ? 'border-gray-700' : 'border-blue-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              className="flex items-center space-x-4 cursor-pointer"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              onClick={() => scrollToSection('home')}
            >
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
                <Tent className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  CampSite Checker
                </h1>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <button
                    onClick={item.action}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                      isDarkMode 
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </button>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              className="flex items-center space-x-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              <div className="hidden md:flex items-center space-x-4">
                <Link
                  to="/login"
                  className={`px-6 py-2 font-semibold transition-colors ${
                    isDarkMode
                      ? 'text-emerald-400 hover:text-emerald-300'
                      : 'text-emerald-600 hover:text-emerald-700'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Get Started
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-900/95' : 'border-gray-200 bg-white/95'} backdrop-blur-lg`}
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={item.action}
                  className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors w-full text-left ${
                    isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.name}</span>
                </button>
              ))}
              <div className="border-t pt-4 mt-4 space-y-2">
                <Link
                  to="/login"
                  className="block px-3 py-3 text-center text-emerald-600 hover:text-emerald-700 font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="block px-3 py-3 text-center bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" ref={heroRef} className="relative z-10 pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="flex items-center justify-center space-x-4 mb-8">
                <Mountain className="w-16 h-16 text-emerald-600" />
                <Trees className="w-16 h-16 text-teal-600" />
                <Sun className="w-16 h-16 text-yellow-500" />
              </div>
              
              <h1 className={`text-5xl lg:text-7xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Find Your Perfect 
                <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Camping Spot
                </span>
              </h1>
              
              <p className={`text-xl lg:text-2xl max-w-4xl mx-auto mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Discover available campsites in real-time, share with friends, and plan unforgettable outdoor adventures with our advanced campground finder.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
            >
              <Link
                to="/register"
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-lg font-bold rounded-2xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 flex items-center space-x-2"
              >
                <span>Start Exploring</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              
              <Link
                to="/login"
                className="px-8 py-4 border-2 border-emerald-500 text-emerald-600 text-lg font-bold rounded-2xl hover:bg-emerald-50 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Sign In</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className={`relative z-10 py-20 ${isDarkMode ? 'bg-gray-800/30' : 'bg-white/30'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={aboutInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className={`text-4xl lg:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              About CampSite Checker
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Revolutionizing the way outdoor enthusiasts discover and secure their perfect camping experiences across America's most beautiful destinations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
            {aboutFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={aboutInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="flex justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={aboutInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className={`${isDarkMode ? 'bg-gray-800/60' : 'bg-white/60'} backdrop-blur-sm rounded-3xl p-8 lg:p-12 border ${isDarkMode ? 'border-gray-700/50' : 'border-white/20'}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  The Story Behind CampSite Checker
                </h3>
                <p className={`text-lg leading-relaxed mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Born from the frustration of missing out on dream camping spots, CampSite Checker was created by a team of passionate outdoor enthusiasts who understood the pain of constantly refreshing booking websites and finding everything sold out.
                </p>
                <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We built this platform to solve the real problems campers face: finding available sites, getting timely notifications, and sharing discoveries with fellow adventurers. Today, we're proud to help thousands of outdoor lovers secure their perfect camping experiences.
                </p>
              </div>
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Beautiful campsite with mountain view"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className={`relative z-10 py-20 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className={`text-4xl lg:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Why Choose CampSite Checker?
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Experience the most advanced campground search platform with features designed for modern outdoor enthusiasts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border ${isDarkMode ? 'border-gray-700/50 hover:border-emerald-500/30' : 'border-white/20 hover:border-emerald-200'} group`}
              >
                <div className="text-emerald-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={`leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section ref={statsRef} className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={statsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className={`text-4xl lg:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Trusted by Thousands
            </h2>
            <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Join our growing community of outdoor enthusiasts
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={statsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Questions Section */}
      <section id="questions" ref={questionsRef} className={`relative z-10 py-20 ${isDarkMode ? 'bg-gray-800/30' : 'bg-white/30'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={questionsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center space-x-3 mb-6">
              <HelpCircle className="w-12 h-12 text-emerald-600" />
              <h2 className={`text-4xl lg:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Frequently Asked Questions
              </h2>
            </div>
            <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Everything you need to know about CampSite Checker
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={questionsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`${isDarkMode ? 'bg-gray-800/60' : 'bg-white/60'} backdrop-blur-sm rounded-2xl border ${isDarkMode ? 'border-gray-700/50' : 'border-white/20'} overflow-hidden`}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-emerald-50/10 transition-colors"
                >
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {faq.question}
                  </h3>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-emerald-600" />
                  )}
                </button>
                {openFAQ === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6"
                  >
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Start Your Adventure?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join thousands of campers who trust CampSite Checker to discover their next outdoor adventure.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-emerald-600 text-lg font-bold rounded-2xl hover:bg-gray-50 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105"
            >
              <span>Get Started Free</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 ${isDarkMode ? 'bg-black' : 'bg-gray-900'} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="md:col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-lg">
                  <Tent className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">CampSite Checker</h3>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                The most advanced campground search platform designed for modern outdoor enthusiasts. Find, share, and book your perfect camping experience.
              </p>
              <div className="flex space-x-4">
                <Mountain className="w-8 h-8 text-gray-600" />
                <Trees className="w-8 h-8 text-green-600" />
                <Sun className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            
            {/* Features */}
            <div>
              <h4 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span>Features</span>
              </h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Real-time availability</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Smart notifications</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Multi-location search</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Mobile optimized</span>
                </li>
              </ul>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => scrollToSection('about')}
                    className="hover:text-white transition-colors flex items-center space-x-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>About Us</span>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('features')}
                    className="hover:text-white transition-colors flex items-center space-x-2"
                  >
                    <Zap className="w-4 h-4" />
                    <span>Features</span>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('questions')}
                    className="hover:text-white transition-colors flex items-center space-x-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>Questions</span>
                  </button>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-emerald-400" />
                <span>Contact Us</span>
              </h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Address</p>
                    <p className="text-sm">
                      548 Market Street,<br />
                      San Francisco, CA 94104
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Phone</p>
                    <a href="tel:+12097648330" className="text-sm hover:text-white transition-colors">
                      +1 (209) 764-8330
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <a href="mailto:info@gizmofacts.com" className="text-sm hover:text-white transition-colors">
                      info@gizmofacts.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © {new Date().getFullYear()} CampSite Checker. Powered by{" "}
              <a
                href="https://gizmofacts.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Gizmofacts
              </a>.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;